/**
 * SubagentModelToggle — the chat-header control for session model pinning.
 *
 * ON  — the model you picked in the header governs EVERY execution in the turn:
 *       graph nodes, cognitive children, delegate/execute_plan fan-outs, and the
 *       internal one-shots (derivation, evaluation, compaction).
 * OFF — each of those executions runs on its own tuned default instead.
 *
 * PURE and PROPS-DRIVEN with zero derivation: every visual state is reachable
 * from props alone, which is what makes each of them a Storybook story rather
 * than something only a live backend can produce. It owns no query, no mutation
 * and no default — the caller decides whether a flip writes a draft or PATCHes a
 * persisted session (the ProcessSelector branch), and the BACKEND owns the
 * default value (the client never fabricates one).
 *
 * The label deliberately says "takes effect on the next run": the pin is
 * snapshotted at turn start, so flipping it mid-turn governs the next
 * turn, never the one in flight. Mirrors the ProcessSelector title precedent.
 */

import { Tooltip } from '@localagent/ui/components/tooltip';

interface SubagentModelToggleProps {
  /** Current state. Render `draft.pinModelToTurn ?? false` for a draft chat. */
  checked: boolean;
  /** Called with the INVERTED value. */
  onChange: (checked: boolean) => void;
  /**
   * The session's effective model, shown in the tooltip so the user can see
   * WHICH model the pin would propagate. Undefined while it is still resolving
   * — the tooltip then names no model rather than guessing one.
   */
  modelName?: string;
  /** Disabled while the session record is still loading. */
  disabled?: boolean;
}

export function SubagentModelToggle({
  checked,
  onChange,
  modelName,
  disabled = false,
}: SubagentModelToggleProps) {
  const target = modelName ? `“${modelName}”` : 'this session’s model';
  const hint = checked
    ? `Sub-agents run on ${target}. Takes effect on the next run.`
    : `Sub-agents run on the models configured for their specialized roles. Turn on to use ${target} for all of them. Takes effect on the next run.`;

  return (
    <label
      data-testid="subagent-model-toggle"
      className="flex w-full cursor-pointer items-center justify-between gap-3 rounded px-2 py-1.5 text-xs hover:bg-accent"
    >
      <Tooltip label={hint} side="left" multiline>
        <span data-testid="subagent-model-toggle-label" className="whitespace-nowrap">
          Use this model for sub-agents
        </span>
      </Tooltip>
      <input
        data-testid="subagent-model-toggle-input"
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="cursor-pointer"
      />
    </label>
  );
}
